import React, { useEffect, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';
import { useSound } from '../../utils/sound/SoundProvider';

function BoxesGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }) {
  // Track which boxes have just been completed for animation
  const [justCompleted, setJustCompleted] = useState([]);
  const [flashEdges, setFlashEdges] = useState([]); // [{edgeId, flashCount}]
  const prevBoxesRef = useRef({});
  const prevEdgesRef = useRef({});
  const sound = useSound();

  // Detect newly completed boxes
  useEffect(() => {
    const prevBoxes = prevBoxesRef.current;
    const currBoxes = gameState.boxes || {};
    const newBoxes = [];
    for (const key in currBoxes) {
      if (!prevBoxes[key] && currBoxes[key]) {
        newBoxes.push(key);
      }
    }
    if (newBoxes.length > 0) {
      setJustCompleted(newBoxes);
      // Play sound using SoundProvider (respects mute)
      if (sound && typeof sound.play === 'function') {
        sound.play('ui:positive');
      }
      // Remove animation after a short delay
      setTimeout(() => setJustCompleted([]), 700);
    }
    prevBoxesRef.current = { ...currBoxes };
  }, [gameState.boxes, sound]);

  // Detect newly claimed edges by other players and flash them
  useEffect(() => {
    const prevEdges = prevEdgesRef.current;
    const currEdges = gameState.edges || {};
    const newClaimed = [];
    for (const key in currEdges) {
      if (!prevEdges[key] && currEdges[key]) {
        // Claimed now, was unclaimed before
        if (currEdges[key] !== user.socketId) {
          newClaimed.push(key);
        }
      }
    }
    if (newClaimed.length > 0) {
      // Flash each new edge twice
      setFlashEdges(flashEdges => [
        ...flashEdges,
        ...newClaimed.map(edgeId => ({ edgeId, flashCount: 2 }))
      ]);
    }
    prevEdgesRef.current = { ...currEdges };
  }, [gameState.edges, user.socketId]);

  // Handle edge flash animation timing
  useEffect(() => {
    if (flashEdges.length === 0) return;
    const timer = setTimeout(() => {
      setFlashEdges(flashEdges =>
        flashEdges.flatMap(({ edgeId, flashCount }) =>
          flashCount > 1 ? [{ edgeId, flashCount: flashCount - 1 }] : []
        )
      );
    }, 250); // flash duration
    return () => clearTimeout(timer);
  }, [flashEdges]);
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });

  const rows = Number(gameState.rows) || 3; // number of boxes vertically
  const cols = Number(gameState.cols) || 3; // number of boxes horizontally

  // Visual sizing: spacing between adjacent dot centers
  const spacing = 56; // px between dot centers (tunable)
  const dotSize = 10; // diameter of dot
  const edgeThickness = 8; // thickness of edge hit area

  const gridCols = cols * 2 + 1;
  const gridRows = rows * 2 + 1;

  // helpers to map UI positions to server edge ids
  const horizEdgeId = (dotR, dotC) => {
    // horizontal edge between dot(dotR,dotC) and dot(dotR,dotC+1)
    // maps to top edge of box at (dotR, dotC) if dotR < rows
    // otherwise maps to bottom edge of box at (dotR-1, dotC)
    if (dotC >= cols) return null; // no horizontal edge beyond final dot
    if (dotR < rows) return `${dotR}_${dotC}_t`;
    return `${dotR - 1}_${dotC}_b`;
  };

  const vertEdgeId = (dotR, dotC) => {
    // vertical edge between dot(dotR,dotC) and dot(dotR+1,dotC)
    // maps to left edge of box at (dotR, dotC) if dotC < cols
    // otherwise maps to right edge of box at (dotR, dotC-1)
    if (dotR >= rows) return null; // no vertical edge beyond final dot
    if (dotC < cols) return `${dotR}_${dotC}_l`;
    return `${dotR}_${dotC - 1}_r`;
  };

  const clickClaim = (edgeId) => {
    if (!edgeId) return;
    submitAction({ type: 'claim-edge', edgeId }).catch(() => {});
  };

  // helpers to check ownership
  const edgeOwner = (id) => (gameState.edges && id ? gameState.edges[id] : null);
  const boxOwner = (br, bc) => (gameState.boxes ? gameState.boxes[`${br}_${bc}`] : null);

  // Build a simple inline grid using CSS grid where columns alternate dot/edge/dot/edge/...
  const templateCols = [];
  for (let c = 0; c < cols; c++) {
    templateCols.push(`${dotSize}px`);
    templateCols.push(`${spacing - dotSize}px`);
  }
  templateCols.push(`${dotSize}px`); // final dot

  const templateRows = [];
  for (let r = 0; r < rows; r++) {
    templateRows.push(`${dotSize}px`);
    templateRows.push(`${spacing - dotSize}px`);
  }
  templateRows.push(`${dotSize}px`);

  return (
    <div className="boxes-game flex items-center justify-center min-h-[220px]">
      <div className="overflow-hidden rounded-[28px] border border-cyan-200/70 bg-gradient-to-br from-cyan-50 via-white to-amber-50 shadow-[0_24px_70px_rgba(15,23,42,0.12)] dark:border-cyan-900/60 dark:from-slate-950 dark:via-slate-900 dark:to-cyan-950/80 p-4 sm:p-6 flex flex-col items-center">
        <div
          className="inline-block bg-transparent"
          style={{
            display: 'grid',
            gridTemplateColumns: templateCols.join(' '),
            gridTemplateRows: templateRows.join(' '),
            gap: 0,
          }}
        >
          {Array.from({ length: gridRows }).map((_, gr) =>
            Array.from({ length: gridCols }).map((_, gc) => {
              const isDot = gr % 2 === 0 && gc % 2 === 0;
              const isHorizEdge = gr % 2 === 0 && gc % 2 === 1;
              const isVertEdge = gr % 2 === 1 && gc % 2 === 0;
              const isBox = gr % 2 === 1 && gc % 2 === 1;

              if (isDot) {
                return (
                  <div key={`d-${gr}-${gc}`} className="flex items-center justify-center">
                    <div
                      style={{ width: dotSize, height: dotSize, borderRadius: '50%' }}
                      className="bg-gray-900 dark:bg-gray-200"
                    />
                  </div>
                );
              }

              if (isHorizEdge) {
                const dotR = gr / 2; // integer
                const dotC = (gc - 1) / 2; // left dot column
                const eid = horizEdgeId(dotR, dotC);
                const owner = edgeOwner(eid);
                const disabled = !eid || owner || gameState.currentPlayer !== user.socketId || gameState.finished;
                // Flash if in flashEdges
                const flash = flashEdges.some(f => f.edgeId === eid);
                return (
                  <div key={`h-${gr}-${gc}`} className="flex items-center justify-center">
                    <button
                      onClick={() => clickClaim(eid)}
                      disabled={disabled}
                      className={`w-full h-full transition-opacity ${flash ? 'animate-pulse bg-amber-300 dark:bg-amber-500' : owner ? 'bg-red-600 dark:bg-red-500' : 'bg-gray-200 dark:bg-gray-700 hover:bg-amber-300 dark:hover:bg-amber-600'}`}
                      style={{ height: edgeThickness, width: '100%', border: 'none', padding: 0 }}
                    />
                  </div>
                );
              }

              if (isVertEdge) {
                const dotR = (gr - 1) / 2; // top dot row
                const dotC = gc / 2; // integer
                const eid = vertEdgeId(dotR, dotC);
                const owner = edgeOwner(eid);
                const disabled = !eid || owner || gameState.currentPlayer !== user.socketId || gameState.finished;
                // Flash if in flashEdges
                const flash = flashEdges.some(f => f.edgeId === eid);
                return (
                  <div key={`v-${gr}-${gc}`} className="flex items-center justify-center">
                    <button
                      onClick={() => clickClaim(eid)}
                      disabled={disabled}
                      className={`h-full w-full transition-opacity ${flash ? 'animate-pulse bg-amber-300 dark:bg-amber-500' : owner ? 'bg-red-600 dark:bg-red-500' : 'bg-gray-200 dark:bg-gray-700 hover:bg-amber-300 dark:hover:bg-amber-600'}`}
                      style={{ width: edgeThickness, height: '100%', border: 'none', padding: 0 }}
                    />
                  </div>
                );
              }

              if (isBox) {
                const br = (gr - 1) / 2;
                const bc = (gc - 1) / 2;
                const owner = boxOwner(br, bc);
                if (!owner) return (
                  <div key={`b-${gr}-${gc}`} className="flex items-center justify-center" />
                );
                // Animate if just completed
                const boxKey = `${br}_${bc}`;
                const animate = justCompleted.includes(boxKey);
                const player = gamePlayers.find(p => p.socketId === owner) || {};
                const color = player.color || '#2563eb'; // fallback blue-600
                const icon = player.icon || null;
                const initials = (player.username || 'Player').split(' ').map(s => s[0]).slice(0,2).join('').toUpperCase();
                // choose text color with basic luminance check for contrast
                const textColor = (hex => {
                  try {
                    const c = hex.replace('#','');
                    const r = parseInt(c.substring(0,2),16);
                    const g = parseInt(c.substring(2,4),16);
                    const b = parseInt(c.substring(4,6),16);
                    const lum = 0.2126*r + 0.7152*g + 0.0722*b;
                    return lum > 160 ? '#111827' : '#ffffff';
                  } catch(e) { return '#ffffff'; }
                })(color);

                return (
                  <div key={`b-${gr}-${gc}`} className="flex items-center justify-center">
                    <div
                      className={`w-full h-full flex items-center justify-center rounded transition-transform duration-500 ${animate ? 'animate-box-fill' : ''}`}
                      style={{ background: color }}
                    >
                      <div className="text-sm font-semibold" style={{ color: textColor }}>
                        {icon || initials}
                      </div>
                    </div>
                  </div>
                );
              }

              return <div key={`x-${gr}-${gc}`} />;
            })
          )}
        </div>
        {/* <div className="mt-3 flex gap-3">
          {gamePlayers.map(p => (
          <div key={p.socketId} className="px-3 py-1 bg-gray-100 dark:bg-gray-900 rounded">
            <div className="text-sm font-medium  text-gray-700 dark:text-gray-300">{p.username}</div>
            <div className="text-xs text-gray-700 dark:text-gray-300">Score: {gameState.scores && (gameState.scores[p.socketId] || 0)}</div>
          </div>
        ))}
      </div> */}
      </div>
    </div>
  );
}

BoxesGame.Guide = function BoxesGuide(){
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Boxes (Dots and Boxes)</h3>
      <ol className="list-decimal ml-6">
        <li>Players take turns claiming a single edge between dots.</li>
        <li>Complete all four edges of a box to claim it and score a point.</li>
        <li>If you claim a box you get another turn.</li>
        <li>When all edges are claimed, highest score wins.</li>
      </ol>
    </div>
  );
};

export default BoxesGame;
